import React from 'react'
import {
  faChevronRight,
  faInfoCircle,
  faLock,
  faStar,
  faThumbtack,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link } from 'react-router-dom'
import { Pill } from '../Text/Pill'
import { twMerge } from 'tailwind-merge'
import { Icon } from '../Icon'
import { Tooltip } from '../Text'
import { ApplicationRoutes } from '@unimpaired/utils'

export interface PageNavCardLinkProps {
  to: ApplicationRoutes
  name: string
  description?: string
  icon?: IconDefinition
  condensed?: boolean
  disabled?: boolean
  hidden?: boolean
  comingSoon?: boolean
  onClick?: () => void
  permissionRequired?: boolean
  children?: React.ReactNode
  navItemTextClasses?: string
  className?: string
  containerClasses?: string
  pinned?: boolean
  onPin?: () => void
  /**
   * When true, applies a keyboard-selected highlight (matches the hover state
   * plus a ring). Receive this as a boolean so React.memo can short-circuit
   * on the 20 unselected siblings when only the selected index changes.
   */
  selected?: boolean
}
const PageNavCardLinkImpl = (props: PageNavCardLinkProps) => {
  if (props.hidden) return null

  const icon = props.permissionRequired ? faLock : props.icon

  if (props.permissionRequired)
    return (
      <div className={twMerge('relative w-full max-w-2xl', props.className)}>
        <button
          disabled
          className={
            'bg-base-500 z-0 w-full relative bg-opacity-40 flex max-w-2xl items-center justify-between rounded-lg p-4 shadow-sm h-full overflow-hidden'
          }>
          <Content {...props} icon={icon} />
        </button>
        {props.permissionRequired && (
          <Pill containerClasses='absolute -top-1 right-2' color='red'>
            Permission Required
          </Pill>
        )}
      </div>
    )
  if (props.comingSoon)
    return (
      <div className={twMerge('relative w-full max-w-2xl', props.className)}>
        <button
          disabled
          className={
            'bg-base-500 z-0 w-full relative bg-opacity-40 flex max-w-2xl items-center justify-between rounded-lg p-4 shadow-sm h-full overflow-hidden'
          }>
          <Content {...props} icon={icon} />
        </button>
        {props.comingSoon && (
          <p className='absolute max-w-2xl z-10 -top-1 right-2 bg-yellow-200 text-yellow-800 min-w-fit py-0.5 flex items-center px-2 font-semibold text-xs rounded-full'>
            Coming Soon!
          </p>
        )}
      </div>
    )
  return (
    <div
      className={twMerge('flex w-full items-center ', props.containerClasses)}>
      {props.onPin && (
        <Tooltip
          tooltipContent={
            props.pinned ? 'Unpin from sidebar' : 'Pin to sidebar'
          }>
          <button
            tabIndex={-1}
            onClick={props.onPin}
            className={twMerge(
              'text-white rotate-45 pr-4',
              props.pinned &&
                'text-yellow-500 rotate-0 hover:line-through hover:text-red-200 transition',
            )}>
            <Icon icon={props.pinned ? faStar : faThumbtack}></Icon>
          </button>
        </Tooltip>
      )}
      <Link
        onClick={props.onClick}
        to={props.to}
        className={twMerge(
          'border w-full bg-base-0 flex max-w-2xl items-center justify-between rounded-lg p-4 shadow-sm h-full group transition hover:bg-primary-50 hover:scale-[102%] hover:shadow-md group overflow-hidden',
          'dark:bg-transparent dark:hover:bg-slate-700/50',
          props.selected &&
            'bg-primary-50 scale-[102%] shadow-md dark:bg-slate-700/60 ring-2 ring-primary-300 dark:ring-indigo-300',
          props.className,
        )}>
        <Content {...props} icon={icon} />
      </Link>
    </div>
  )
}

// Memoized to skip re-renders when a sibling's highlight changes (only the affected cards re-render).
export const PageNavCardLink = React.memo(PageNavCardLinkImpl)

const Content = (props: PageNavCardLinkProps) => (
  <>
    {props.icon && (
      <FontAwesomeIcon
        icon={props.icon}
        className='text-primary-800 group-hover:text-primary-600 dark:group-hover:text-indigo-200 h-7! w-7! pr-4'
      />
    )}

    <div className={twMerge(`w-full max-w-2xl`, props.condensed && 'flex')}>
      <p
        className={twMerge(
          'text-primary-800 group-hover:text-primary-400 dark:group-hover:text-indigo-200 text-left font-medium',
        )}>
        {props.name}
      </p>
      {props.description && (
        <div className='flex'>
          <p
            className={twMerge(
              'text-xs text-base-700 group-hover:text-base-300 w-3/4 mt-1 text-left',
              'dark:group-hover:text-base-700',
              props.condensed && 'text-xs pl-1 w-full',
              props.condensed && 'hidden',
            )}>
            {props.description}
          </p>
        </div>
      )}
      {props.condensed && props.description && (
        <Tooltip hoverDelay={0} tooltipContent={props.description}>
          <Icon
            className='pl-2 !h-3 !w-3 text-white/90'
            icon={faInfoCircle}></Icon>
        </Tooltip>
      )}
      {props.children}
    </div>

    {!props.comingSoon && !props.permissionRequired && (
      <FontAwesomeIcon
        icon={faChevronRight}
        className={twMerge(
          'transition group-hover:translate-x-1 text-base-700 group-hover:text-indigo-100 group-hover:scale-110',
        )}
      />
    )}
  </>
)
